class CollectionScrollRestore {
  static LAST_CLICKED_ID_KEY = `LAST_CLICKED_ID_KEY-${window.location.pathname}`;
  static LAST_SCROLL_KEY = `LAST_SCROLL_KEY-${window.location.pathname}`;

  constructor() {
    // Manualy handle scroll restoration
    if ("scrollRestoration" in history) history.scrollRestoration = "manual";
    document.addEventListener("click", this.setLatestClickedProduct.bind(this));
    window.addEventListener("pagehide", this.saveClickedProductIdInSession.bind(this));
    window.addEventListener("pagehide", this.saveScrollPosition.bind(this));
    this.restoreScrollOnLoad();
  }

  setLatestClickedProduct(event) {
    if (event === undefined) event = window.event;
    this.clickedGridId = event.target.closest(".grid__item") && event.target.closest(".grid__item").id;
  }

  saveClickedProductIdInSession() {
    if (this.clickedGridId) sessionStorage.setItem(CollectionScrollRestore.LAST_CLICKED_ID_KEY, this.clickedGridId);
  }

  saveScrollPosition() {
    sessionStorage.setItem(CollectionScrollRestore.LAST_SCROLL_KEY, window.scrollY);
  }

  restoreScrollOnLoad() {
    const scrollY = sessionStorage.getItem(CollectionScrollRestore.LAST_SCROLL_KEY);
    if (!scrollY) return;
    const scrollMaxY =
      window.scrollMaxY || document.documentElement.scrollHeight - document.documentElement.clientHeight;

    if (scrollY < scrollMaxY) {
      window.scrollTo(window.scrollX, scrollY);
      sessionStorage.removeItem(CollectionScrollRestore.LAST_SCROLL_KEY);
      return;
    }

    this.restoreScrollById();
  }

  restoreScrollById() {
    const id = sessionStorage.getItem(CollectionScrollRestore.LAST_CLICKED_ID_KEY);
    if (!id) return;
    const scrollToElem = document.getElementById(id);
    if (!scrollToElem) return;
    scrollToElem.scrollIntoView({ behavior: "auto", block: "center" });
    sessionStorage.removeItem(CollectionScrollRestore.LAST_CLICKED_ID_KEY);
  }
}

class CollectionInfiniteScroll extends HTMLElement {
  static INFINITE_SCROLL_OFFSET = 600;
  static SCROLL_END_DEBOUNCE = 10; // SCROLL_END_DEBOUNCE should be LARGER then SCROLL_DEBOUNCE
  static SCROLL_DEBOUNCE = 0;
  static PAGE_CACHE_KEY = `PAGE_CACHE_KEY`;
  static VARIANT_SELECTION_KEY = `VARIANT_SELECTION_KEY-${window.location.pathname}`;

  constructor() {
    super();

    // Element
    this.getCollectionElem = () => document.getElementById("CollectionProductGrid").querySelector(".collection");
    this.getCollectionListElem = () => document.getElementById("main-collection-product-grid");

    // Loading Products
    this.cachedData = this.getPagesFromStorage();
    this.loadPreviousPagesFromCache();
    this.loadVariantSelection();
    this.saveInitialPageToCache();
    window.addEventListener("pagehide", () => this.savePagesToStorage(this.cachedData));
    window.addEventListener("pagehide", this.saveSwatchInput.bind(this));

    // Scroll handlers
    this.debouncedOnScrollDown = debounce(
      () => this.onPageEndScrollHandler({ isScrollDown: true }),
      CollectionInfiniteScroll.SCROLL_END_DEBOUNCE
    );
    this.debouncedOnScrollUp = debounce(
      () => this.onPageEndScrollHandler({ isScrollDown: false }),
      CollectionInfiniteScroll.SCROLL_END_DEBOUNCE
    );
    this.debouncedOnScroll = debounce(() => this.onScrollHandler(), CollectionInfiniteScroll.SCROLL_DEBOUNCE);
    window.oldScroll = window.scrollY;
    window.addEventListener("scroll", this.handleScroll.bind(this));

    // Restore scroll position
    this.restoreScrollHandler = new CollectionScrollRestore();
  }

  handleScroll() {
    this.handlePaginationOnScrollEnd();
    this.debouncedOnScroll();
  }

  handlePaginationOnScrollEnd() {
    const isScrollEnd =
      window.innerHeight + window.scrollY >=
      document.body.offsetHeight - CollectionInfiniteScroll.INFINITE_SCROLL_OFFSET;
    const isScrollStart = window.scrollY < CollectionInfiniteScroll.INFINITE_SCROLL_OFFSET;
    const isScrollDown = window.oldScroll < window.scrollY;
    const isScrollUp = window.oldScroll > window.scrollY;

    if (isScrollEnd && isScrollDown && !this.getIsLoading()) this.debouncedOnScrollDown();
    if (isScrollStart && isScrollUp && !this.getIsLoading()) this.debouncedOnScrollUp();
    window.oldScroll = window.scrollY;
  }

  onScrollHandler() {
    const pageIndexElements = document.querySelectorAll("[data-grid-page]");
    let currentPage = 1;
    pageIndexElements.forEach(function (elem) {
      const isVisible = isScrolledPast(elem);
      if (isVisible) {
        const visiblePage = parseInt(elem.dataset.gridPage);
        if (visiblePage > currentPage) currentPage = visiblePage;
      }
    });

    const searchParams = new URL(location.href).searchParams;
    searchParams.set("page", currentPage);
    this.updateURLHash(searchParams.toString());
  }

  onPageEndScrollHandler({ isScrollDown }) {
    const searchParams = new URL(location.href).searchParams;
    const currentPage = this.getCurrentPageFromURL();

    if (isScrollDown) {
      searchParams.set("page", currentPage + 1);
      this.render(searchParams.toString(), true);
    } else if (!isScrollDown && currentPage > 1) {
      searchParams.set("page", currentPage - 1);
      this.render(searchParams.toString(), false);
    }
  }

  async render(searchParams, isScrollDown) {
    this.getCollectionElem().classList.add("loading");
    const section = this.getSection();

    const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
    const html = await this.getHtmlFromCacheOrFetch(url);
    const isRendered = await this.renderPage({ html, isScrollDown, restoreScroll: true });

    if (isRendered) this.updateURLHash(searchParams);
    this.getCollectionElem().classList.remove("loading");
  }

  async renderPage({ html, isScrollDown, restoreScroll }) {
    const parsedHtml = new DOMParser().parseFromString(html, "text/html");

    const isNewPageEmpty = !!parsedHtml.getElementById("empty-grid");
    const isPageAlreadyRendered = this.isPageAlreadyRendered(parsedHtml);
    if (isNewPageEmpty || isPageAlreadyRendered) return false;

    const gridElem = this.getCollectionListElem();
    const newListItems = parsedHtml.getElementById("main-collection-product-grid");
    if (isScrollDown) {
      this.appendElem(gridElem, newListItems);
    } else {
      // Get page to scroll to
      const latestPageElem = gridElem.querySelector("[data-grid-page]");
      const latestPage = latestPageElem && latestPageElem.dataset.gridPage;

      this.prependElem(gridElem, newListItems);

      // Scroll to page
      if (restoreScroll) {
        const elemToScroll = this.getCollectionElem().querySelector(`[data-grid-page='${latestPage}']`);
        if (elemToScroll) elemToScroll.scrollIntoView({ behavior: "auto", block: "end" });
      }
    }

    return true;
  }

  async getHtmlFromCacheOrFetch(url) {
    const cachedItem = this.cachedData.find((data) => data.url === url);
    if (cachedItem) {
      return cachedItem.html;
    } else {
      const html = await fetch(url).then((response) => response.text());
      this.addPageToCache({ url, html });
      return html;
    }
  }

  isPageAlreadyRendered(parsedHtml) {
    const parsedHtmlElem = parsedHtml.getElementById("main-collection-product-grid");
    if (!parsedHtmlElem) return;
    return this.getCollectionElem().innerHTML.includes(parsedHtmlElem.innerHTML);
  }

  updateURLHash(searchParams) {
    const currentSearchParams = new URL(location.href).searchParams.toString();
    if (searchParams !== currentSearchParams) {
      history.pushState({ searchParams }, "", `${window.location.pathname}${searchParams && "?".concat(searchParams)}`);
    }
  }

  getSection() {
    return {
      id: "main-collection-product-grid",
      section: document.getElementById("main-collection-product-grid").dataset.id,
    };
  }

  appendElem(parentElem, newElem) {
    parentElem.innerHTML += newElem.innerHTML;
  }

  prependElem(parentElem, newElem) {
    parentElem.innerHTML = newElem.innerHTML + parentElem.innerHTML;
  }

  getCurrentPageFromURL() {
    const searchParams = new URL(location.href).searchParams;
    return parseInt(searchParams.get("page")) || 1;
  }

  getIsLoading() {
    return this.getCollectionElem().classList.contains("loading");
  }

  saveSwatchInput() {
    const variantInputs = [];
    document.querySelectorAll(".color-swatch__radio").forEach((elem) => {
      if (elem.checked) variantInputs.push(elem.dataset.colorId);
    });
    sessionStorage.setItem(CollectionInfiniteScroll.VARIANT_SELECTION_KEY, JSON.stringify(variantInputs));
  }

  loadPreviousPagesFromCache() {
    if (this.cachedData.length === 0) return;
    const section = this.getSection();
    const searchParams = new URL(location.href).searchParams;
    const page = searchParams.get("page");
    if (!page) return;

    for (let i = page - 1; i > 0; i--) {
      searchParams.set("page", i);
      const urlKey = `${window.location.pathname}?section_id=${section.section}&${searchParams.toString()}`;
      const cachedObject = this.cachedData.find((data) => data.url === urlKey);
      if (!cachedObject) return;
      this.renderPage({ html: cachedObject.html, isScrollDown: false, restoreScroll: false });
    }
  }

  loadVariantSelection() {
    const variantInputsString = sessionStorage.getItem(CollectionInfiniteScroll.VARIANT_SELECTION_KEY);
    if (!variantInputsString) return;
    const variantInputs = JSON.parse(variantInputsString);
    variantInputs.forEach((colorId) => {
      const inputElems = document.querySelectorAll(`[data-color-id="${colorId}"]`);
      if (!inputElems || inputElems.length === 0) return;
      inputElems.forEach((inputElem) => {
        const imageElem = inputElem.closest(".card-wrapper").querySelector("img");
        const linkElem = inputElem.closest(".card-wrapper").querySelector("a");
        const productColorSwatch = inputElem.closest("product-card-color-swatch");

        if (!productColorSwatch) return;
        setTimeout(() => productColorSwatch.selectElem({ inputElem, imageElem, linkElem }), 0);
      });
    });
  }

  /**
   * The first page does not page the page param, so it is not saved to cached
   * this function detects the first page and saves it to cache`
   */
  saveInitialPageToCache() {
    const searchParams = new URL(location.href).searchParams;
    const page = parseInt(searchParams.get("page"));
    // Assume page = 1 if page param doesn't exist
    if (!page) {
      const firstPageHtml = this.getCollectionElem().innerHTML;
      const section = this.getSection();
      searchParams.set("page", 1);
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams.toString()}`;
      this.addPageToCache({ url: url, html: firstPageHtml });
    }
  }

  getPagesFromStorage() {
    const cachedDataString = sessionStorage.getItem(CollectionInfiniteScroll.PAGE_CACHE_KEY);
    if (!cachedDataString) return [];
    const cachedData = JSON.parse(cachedDataString);
    const path = this.getPath();
    if (cachedData.currentPath !== path) return [];
    return cachedData.pages;
  }

  addPageToCache(page) {
    try {
      const pages = [...this.cachedData, page];
      this.savePagesToStorage(pages);
      this.cachedData = [...this.cachedData, page];
    } catch {
      // Size exceeded
      console.log("LOG:CACHE SIZE EXCEEDED");
      try {
        const pages = [...this.cachedData.slice(5), page];
        this.savePagesToStorage(pages);
        this.cachedData = [...this.cachedData.slice(5), page];
      } catch {
        console.log("LOG:CACHE CLEARANCE FAILED, EMPTYING CACHE");
        this.savePagesToStorage([]);
        this.cachedData = [];
      }
    }
  }

  savePagesToStorage(pages) {
    if (!pages || pages.length === 0) return;

    const path = this.getPath();
    const pageObj = { currentPath: path, pages: pages };

    sessionStorage.setItem(CollectionInfiniteScroll.PAGE_CACHE_KEY, JSON.stringify(pageObj));
  }

  getPath() {
    return `${window.location.hostname}${window.location.pathname}`;
  }
}

customElements.define("collection-infinite-scroll", CollectionInfiniteScroll);
